/**
 * Axios Configuration and Interceptors for Production
 * Handles authentication, logging, error handling, and performance monitoring
 */

import axios from 'axios'
import logger from '@/services/logger'

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
})

// Request counter for performance monitoring
let activeRequests = 0
const requestTimers = new Map()

/**
 * Request Interceptor
 * Adds authentication, CSRF token, and logging
 */
api.interceptors.request.use(
  (config) => {
    const startTime = performance.now()
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    // Add request ID for tracking
    config.metadata = { startTime, requestId }
    requestTimers.set(requestId, startTime)
    activeRequests++
    
    // Add authentication token
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add CSRF token for non-GET requests
    if (config.method !== 'get') {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = csrfToken
      }
    }
    
    // Add user context if available
    const user = localStorage.getItem('user')
    if (user) {
      try {
        const userData = JSON.parse(user)
        config.headers['X-User-ID'] = userData.id
        config.headers['X-User-Email'] = userData.email
      } catch (error) {
        logger.warn('Failed to parse user data from localStorage', { error: error.message })
      }
    }
    
    // Log API request (only in development for debugging)
    if (import.meta.env.DEV) {
      logger.debug('API Request', {
        method: config.method?.toUpperCase(),
        url: config.url,
        request_id: requestId,
        data: config.data ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data).substring(0, 1000)) : null
      }, 'api')
    }
    
    // Rate limiting check
    if (activeRequests > 10) {
      logger.warn('High number of concurrent requests', {
        active_requests: activeRequests,
        request_id: requestId
      }, 'api')
    }
    
    return config
  },
  (error) => {
    activeRequests--
    logger.error('Request Interceptor Error', {
      error_message: error.message,
      error_stack: error.stack
    }, 'api')
    return Promise.reject(error)
  }
)

/**
 * Response Interceptor
 * Handles responses, errors, logging, and automatic retries
 */
api.interceptors.response.use(
  (response) => {
    const { config } = response
    const { startTime, requestId } = config.metadata || {}
    const duration = startTime ? performance.now() - startTime : 0
    
    activeRequests--
    requestTimers.delete(requestId)
    
    // Log successful API response
    logger.logApiCall(
      `${config.method?.toUpperCase()} ${config.url}`,
      config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : {},
      response.data,
      response.status,
      duration
    )
    
    // Log slow requests
    if (duration > 5000) { // 5 seconds
      logger.warn('Slow API Response', {
        method: config.method?.toUpperCase(),
        url: config.url,
        duration_ms: duration,
        status: response.status,
        request_id: requestId
      }, 'performance')
    }
    
    // Monitor response size
    const responseSize = JSON.stringify(response.data).length
    if (responseSize > 1024 * 1024) { // 1MB
      logger.warn('Large API Response', {
        method: config.method?.toUpperCase(),
        url: config.url,
        size_bytes: responseSize,
        request_id: requestId
      }, 'performance')
    }
    
    return response
  },
  async (error) => {
    const { config } = error
    const { startTime, requestId } = config?.metadata || {}
    const duration = startTime ? performance.now() - startTime : 0
    
    activeRequests--
    if (requestId) {
      requestTimers.delete(requestId)
    }
    
    // Extract error information
    const status = error.response?.status
    const statusText = error.response?.statusText
    const errorData = error.response?.data
    const errorMessage = errorData?.message || error.message
    
    // Log API error
    logger.logApiCall(
      `${config?.method?.toUpperCase()} ${config?.url}`,
      config?.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : {},
      errorData || { error: errorMessage },
      status || 0,
      duration
    )
    
    // Handle specific error types
    switch (status) {
      case 401:
        await handleUnauthorizedError(error)
        break
      case 403:
        handleForbiddenError(error)
        break
      case 404:
        handleNotFoundError(error)
        break
      case 429:
        await handleRateLimitError(error)
        break
      case 422:
        handleValidationError(error)
        break
      case 500:
      case 502:
      case 503:
      case 504:
        await handleServerError(error)
        break
      default:
        if (error.code === 'ECONNABORTED') {
          handleTimeoutError(error)
        } else if (!status) {
          handleNetworkError(error)
        }
    }
    
    // Enhanced error object
    const enhancedError = {
      ...error,
      requestId,
      duration,
      timestamp: new Date().toISOString(),
      userMessage: getUserFriendlyMessage(error),
      isRetryable: isRetryableError(error)
    }
    
    return Promise.reject(enhancedError)
  }
)

/**
 * Handle 401 Unauthorized errors
 */
async function handleUnauthorizedError(error) {
  logger.logSecurityEvent('Unauthorized API Access', {
    url: error.config?.url,
    method: error.config?.method,
    has_token: !!localStorage.getItem('auth_token')
  })

  // Clear authentication data
  localStorage.removeItem('auth_token')
  localStorage.removeItem('user')

  // Check if we're not already on login page
  if (!window.location.pathname.includes('/login')) {
    // Extract locale from current path or use default
    const localeMatch = window.location.pathname.match(/^\/(en|pt-br|es)/)
    const locale = localeMatch ? localeMatch[1] : 'en'

    // Redirect to login with return URL (using locale-prefixed path)
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.href = `/${locale}/auth/login?redirect=${returnUrl}`
  }
}

/**
 * Handle 403 Forbidden errors
 */
function handleForbiddenError(error) {
  logger.logSecurityEvent('Forbidden API Access', {
    url: error.config?.url,
    method: error.config?.method,
    user: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : 'unknown'
  })
}

/**
 * Handle 404 Not Found errors
 */
function handleNotFoundError(error) {
  logger.warn('API Endpoint Not Found', {
    url: error.config?.url,
    method: error.config?.method
  }, 'api')
}

/**
 * Handle 429 Rate Limit errors
 */
async function handleRateLimitError(error) {
  const retryAfter = error.response?.headers['retry-after'] || 60
  
  logger.warn('Rate Limit Exceeded', {
    url: error.config?.url,
    retry_after: retryAfter
  }, 'api')
  
  // Show user-friendly message
  if (window.toast) {
    window.toast.warning(`Too many requests. Please wait ${retryAfter} seconds and try again.`)
  }
}

/**
 * Handle 422 Validation errors
 */
function handleValidationError(error) {
  const validationErrors = error.response?.data?.errors || {}
  
  logger.info('Validation Error', {
    url: error.config?.url,
    errors: validationErrors
  }, 'validation')
}

/**
 * Handle 5xx Server errors
 */
async function handleServerError(error) {
  logger.error('Server Error', {
    url: error.config?.url,
    method: error.config?.method,
    status: error.response?.status,
    error_data: error.response?.data
  }, 'api')
  
  // Implement automatic retry for server errors
  if (error.config && !error.config.__retryCount) {
    error.config.__retryCount = 0
  }
  
  if (error.config.__retryCount < 3) {
    error.config.__retryCount++
    
    // Exponential backoff
    const delay = Math.pow(2, error.config.__retryCount) * 1000
    
    logger.info('Retrying API Request', {
      url: error.config.url,
      attempt: error.config.__retryCount,
      delay_ms: delay
    }, 'api')
    
    await new Promise(resolve => setTimeout(resolve, delay))
    return api(error.config)
  }
}

/**
 * Handle timeout errors
 */
function handleTimeoutError(error) {
  logger.warn('Request Timeout', {
    url: error.config?.url,
    timeout: error.config?.timeout
  }, 'api')
}

/**
 * Handle network errors
 */
function handleNetworkError(error) {
  logger.error('Network Error', {
    message: error.message,
    url: error.config?.url,
    online: navigator.onLine
  }, 'network')
  
  if (!navigator.onLine) {
    if (window.toast) {
      window.toast.error('No internet connection. Please check your network and try again.')
    }
  }
}

/**
 * Get user-friendly error message
 */
function getUserFriendlyMessage(error) {
  const status = error.response?.status
  
  switch (status) {
    case 401:
      return 'Please log in to continue'
    case 403:
      return 'You don\'t have permission to perform this action'
    case 404:
      return 'The requested resource was not found'
    case 422:
      return 'Please check your input and try again'
    case 429:
      return 'Too many requests. Please wait a moment and try again'
    case 500:
      return 'Server error. Our team has been notified'
    case 502:
    case 503:
    case 504:
      return 'Service temporarily unavailable. Please try again later'
    default:
      if (error.code === 'ECONNABORTED') {
        return 'Request timeout. Please try again'
      } else if (!error.response) {
        return 'Network error. Please check your connection'
      }
      return 'An unexpected error occurred'
  }
}

/**
 * Check if error is retryable
 */
function isRetryableError(error) {
  const status = error.response?.status
  return status >= 500 || error.code === 'ECONNABORTED' || !status
}

/**
 * Monitor connection health
 */
function setupConnectionMonitoring() {
  // Monitor online/offline status
  window.addEventListener('online', () => {
    logger.logLifecycleEvent('Connection Restored')
    if (window.toast) {
      window.toast.success('Connection restored')
    }
  })
  
  window.addEventListener('offline', () => {
    logger.logLifecycleEvent('Connection Lost')
    if (window.toast) {
      window.toast.error('Connection lost. Some features may not work')
    }
  })
  
  // Periodic health check
  setInterval(async () => {
    try {
      await api.get('/health', { timeout: 5000 })
    } catch (error) {
      if (error.response?.status !== 404) { // 404 is expected if health endpoint doesn't exist
        logger.warn('Health Check Failed', {
          error: error.message,
          status: error.response?.status
        }, 'health')
      }
    }
  }, 60000) // Every minute
}

// Initialize connection monitoring
setupConnectionMonitoring()

// Export configured axios instance
export default api

// Also export raw axios for special cases
export { axios }

// Export utilities
export {
  getUserFriendlyMessage,
  isRetryableError
}